import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './ArchivPresentations.module.scss';
import folderIcon from '@assets/img/Folder_Green.svg';
import fileIcon from '@assets/img/AdminPanel/fileSolid.svg';
import fileMissingIcon from '@assets/img/AdminPanel/fileDownloadOutline.svg';
import glaz from '@assets/img/glaz.svg';
import arrowTable from '@assets/img/arrowTable.svg';
import {
  getAllArchivePresentation,
  getConfParticipants,
  server,
} from '../../apirequests/apirequests';
import { useTranslation } from 'react-i18next';
import { useWindowWidth } from '../../hooks/hooks';
import PresentationDetailModal from './PresentationDetailModal';

const SWITCHABLE_COLUMNS = [
  { key: 'organization', label: 'Организация' },
  { key: 'direction', label: 'Направление' },
  { key: 'name', label: 'Доклад' },
  { key: 'presentation', label: 'Презентация' },
];

const getConferenceYear = date => {
  const raw = date?.[0]?.value || date?.[0] || date;
  if (!raw) return '';
  return String(raw).slice(0, 4);
};

const toRoman = num => {
  if (!Number.isFinite(num) || num < 1) return '';
  const map = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ];
  let n = Math.floor(num);
  let result = '';
  for (const [value, numeral] of map) {
    while (n >= value) {
      result += numeral;
      n -= value;
    }
  }
  return result;
};

const getConferenceRoman = year => {
  const y = Number(year);
  if (!y) return '';
  return toRoman(y - 2010);
};

const HAS_ROMAN_RE = /\bM{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})\b/;

const getConferenceTitle = conference => {
  const year = getConferenceYear(conference?.date);
  const roman = getConferenceRoman(year);
  const description = conference?.description?.trim();

  if (description) {
    if (!year) return description;
    if (HAS_ROMAN_RE.test(description)) return `${year} ${description}`;
    return roman ? `${year} ${roman} ${description}` : `${year} ${description}`;
  }

  if (!year) return 'Конференция ССПС';
  return roman ? `${year} ${roman} Конференция ССПС` : `${year} Конференция ССПС`;
};

const PresentationCell = ({ presentation, onDownload }) => {
  if (presentation?.url) {
    return (
      <div className={styles.presentationAvailable}>
        <img src={fileIcon} alt="" />
        <a
          href={`${server}/${presentation.url}`}
          onClick={e => {
            e.preventDefault();
            onDownload(presentation.url, presentation.name || 'presentation');
          }}
        >
          {presentation.name || 'Презентация'}
        </a>
      </div>
    );
  }

  return (
    <div className={styles.presentationMissing}>
      <img src={fileMissingIcon} alt="" />
      <span>Отсутствует</span>
    </div>
  );
};

const ArchivPresentations = () => {
  const { t } = useTranslation('leftMenu');
  const navigate = useNavigate();
  const { conferenceId } = useParams();
  const windowWidth = useWindowWidth();
  const [conferences, setConferences] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [selectedConference, setSelectedConference] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeColumnIndex, setActiveColumnIndex] = useState(3);

  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth <= 780;
  const showSwitchableColumn = isTablet && !isMobile;
  const activeColumn = SWITCHABLE_COLUMNS[activeColumnIndex];

  const slideNext = () => {
    setActiveColumnIndex(prev => (prev + 1) % SWITCHABLE_COLUMNS.length);
  };

  const slidePrev = () => {
    setActiveColumnIndex(prev => (prev - 1 + SWITCHABLE_COLUMNS.length) % SWITCHABLE_COLUMNS.length);
  };

  useEffect(() => {
    getAllArchivePresentation().then(resp => {
      setConferences(resp?.data?.conferences || []);
    });
  }, []);

  useEffect(() => {
    if (!conferenceId) {
      setSelectedConference(null);
      setParticipants([]);
      setSelectedRow(null);
      return;
    }
    const current = conferences.find(item => item.id === conferenceId) || { id: conferenceId };
    setSelectedConference(current);
    setLoading(true);
    getConfParticipants(conferenceId, { archive: true })
      .then(res => {
        if (res?.status === 200) {
          setParticipants(res.data.participants || []);
        }
      })
      .finally(() => setLoading(false));
  }, [conferenceId, conferences]);

  const handleDownload = async (fileUrl, fileName) => {
    try {
      const response = await fetch(`${server}/${fileUrl}`);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const renderSwitchableCell = (row, presentation) => {
    if (activeColumn.key === 'presentation') {
      return <PresentationCell presentation={presentation} onDownload={handleDownload} />;
    }
    return row[activeColumn.key] || '—';
  };

  if (conferenceId) {
    return (
      <section className={styles.ArchivPresentations}>
        <h1 className={styles.title}>
          {selectedConference ? getConferenceTitle(selectedConference) : t('presentationArchive')}
        </h1>
        {loading ? (
          <div className={styles.empty}>
            <p>Загрузка…</p>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={isTablet ? styles.tableCompact : undefined}>
              <thead>
                <tr>
                  <th>№</th>
                  <th>ФИО</th>
                  {!isTablet && (
                    <>
                      <th>Организация</th>
                      <th>Направление</th>
                      <th>Доклад</th>
                      <th>Презентация</th>
                    </>
                  )}
                  {showSwitchableColumn && (
                    <th className={styles.switchableTh}>
                      <div className={styles.arrowTableCont}>
                        <button type="button" className={styles.arrowBtn} onClick={slidePrev}>
                          <img src={arrowTable} alt="" />
                        </button>
                        <p>{activeColumn.label}</p>
                        <button type="button" className={styles.arrowBtn} onClick={slideNext}>
                          <img
                            src={arrowTable}
                            alt=""
                            style={{ transform: 'rotate(180deg)' }}
                          />
                        </button>
                      </div>
                    </th>
                  )}
                  {isTablet && <th className={styles.vizionTh} />}
                </tr>
              </thead>
              <tbody>
                {participants.map((row, index) => {
                  const presentation = row.presentation?.[0];
                  return (
                    <tr key={row.id}>
                      <td>{index + 1}</td>
                      <td>
                        {(row.fio || []).map((name, idx) => (
                          <p key={idx}>{name}</p>
                        ))}
                      </td>
                      {!isTablet && (
                        <>
                          <td>{row.organization}</td>
                          <td>{row.direction}</td>
                          <td>{row.name}</td>
                          <td>
                            <PresentationCell
                              presentation={presentation}
                              onDownload={handleDownload}
                            />
                          </td>
                        </>
                      )}
                      {showSwitchableColumn && (
                        <td className={styles.switchableTd}>
                          {renderSwitchableCell(row, presentation)}
                        </td>
                      )}
                      {isTablet && (
                        <td className={styles.vizionTd}>
                          <button
                            type="button"
                            className={styles.vizionBtn}
                            onClick={() => setSelectedRow(row)}
                            title="Подробнее"
                          >
                            <img src={glaz} alt="Подробнее" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!participants.length && (
              <div className={styles.emptyInTable}>
                <p>Презентации отсутствуют</p>
              </div>
            )}
          </div>
        )}
        {selectedRow && (
          <PresentationDetailModal
            data={selectedRow}
            onClose={() => setSelectedRow(null)}
            onDownload={handleDownload}
          />
        )}
      </section>
    );
  }

  return (
    <section className={styles.ArchivPresentations}>
      <h1 className={styles.title}>{t('presentationArchive')}</h1>
      {conferences.length ? (
        <div className={styles.list}>
          {conferences.map(conference => (
            <button
              type="button"
              key={conference.id}
              className={styles.listItem}
              onClick={() => navigate(`/account/archivepresentations/${conference.id}`)}
            >
              <img src={folderIcon} alt="" />
              <span>{getConferenceTitle(conference)}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          <p>Пока нет конференций с презентациями</p>
        </div>
      )}
    </section>
  );
};

export default ArchivPresentations;
